import React, { useState, useEffect, useRef, useContext } from 'react';
import useConversation from './useConversation';
import axios from 'axios';
import API_BASE_URL from '../config';
import { UserContext } from './UserContext';

import Avatar from './Avatar';
import PartnerProfileModal from './PartnerProfileModal';
import ReportModal from './ReportModal';

// ─── Private Match Chat Room ───────────────────────────────────────────────
// Shown when a user opens a chat with one of their successful matches.
// Features:
//   • Real-time text messaging (socket + REST fallback)
//   • Request a video call  → partner gets a pop-up to accept/decline
//   • Request a phone call  → partner gets a pop-up to accept/decline
//   • Incoming call pop-ups handled here too
// ──────────────────────────────────────────────────────────────────────────

const MatchChatRoom = ({ currentUser, match, socket, onClose, onStartVideoCall, onStartPhoneCall }) => {
  const { token: ctxToken } = useContext(UserContext);
  const { messages, draft: newMessage, setDraft: setNewMessage, send: sendMessage, error, sending, reload } = useConversation({ socket, userId: currentUser.id, partnerId: match.partner_id, token: ctxToken });
  const [callPending, setCallPending] = useState(false);  // waiting for partner to accept
  const pendingCall = useRef(false);
  const [viewingProfile, setViewingProfile] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const messagesEndRef = useRef(null);

  // ── Socket listeners ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!socket) return;

    const handleMatchCallAccepted = (data) => {
      if (data.from_user_id !== match.partner_id || !pendingCall.current) return;
      pendingCall.current = false;
      setCallPending(false);
      if (data.call_type === 'video') {
        onStartVideoCall(match.partner_id);
      } else {
        onStartPhoneCall(match.partner_id);
      }
    };

    const handleMatchCallDeclined = (data) => {
      if (data.from_user_id !== match.partner_id) return;
      pendingCall.current = false;
      setCallPending(false);
      alert(`${match.partner_username} דחה/תה את השיחה.`);
    };

    socket.on('match_call_accepted', handleMatchCallAccepted);
    socket.on('match_call_declined', handleMatchCallDeclined);

    return () => {
      socket.off('match_call_accepted', handleMatchCallAccepted);
      socket.off('match_call_declined', handleMatchCallDeclined);
    };
  }, [socket, match, currentUser, onStartVideoCall, onStartPhoneCall]);

  // ── Auto-scroll ───────────────────────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // ── Call requests ─────────────────────────────────────────────────────────
  const requestCall = (callType) => {
    if (!socket) return;
    setCallPending(true);
    pendingCall.current = true;
    socket.emit('match_call_request', {
      to_user_id: match.partner_id,
      from_user_id: currentUser.id,
      from_username: currentUser.username,
      call_type: callType,
    });
  };


  // ── Unmatch handler ──────────────────────────────────────────────────────────
  const handleMatchUnmatch = async () => {
    const authToken = ctxToken || localStorage.getItem('token');
    if (!authToken) return;

    try {
      await axios.post(
        `${API_BASE_URL}/remove_match`,
        {
          session_id: match.session_id,
          partner_id: match.partner_id,
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      onClose(); // close the chat room after unmatching
    } catch (err) {
      console.error('Error unmatching:', err);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="match-chatroom-overlay">
      <div className="match-chatroom">

        {/* Header */}
        <div className="match-chatroom-header">
          <button className="match-chatroom-back" onClick={onClose}>‹</button>
          <div className="match-chatroom-partner">
            <div className="match-chatroom-avatar">
              <Avatar
                src={match.partner_image ? (match.partner_image.startsWith('http') ? match.partner_image : `${API_BASE_URL}${match.partner_image}`) : null}
                alt={match.partner_username}
                placeholder={match.partner_username?.[0]?.toUpperCase() || '?'}
                imgClass="match-chatroom-avatar-img"
                fallbackClass="match-chatroom-avatar-fallback"
              />
            </div>
            <div>
              <h3
              className="match-chatroom-name"
              onClick={() => setViewingProfile(true)}
              style={{ cursor: 'pointer' }}
              title="צפייה בפרופיל"
            >
              {match.partner_username}
            </h3>
              <p className="match-chatroom-sub">שיחה פרטית עם ההתאמה</p>
            </div>
          </div>
          <div className="match-chatroom-call-btns">
            <button
              className="match-call-btn phone"
              onClick={() => requestCall('phone')}
              disabled={callPending}
              title="בקשה לשיחה קולית"
            >
              📞
            </button>
            <button
              className="match-call-btn video"
              onClick={() => requestCall('video')}
              disabled={callPending}
              title="בקשה לשיחת וידאו"
            >
              📹
            </button>
            <button
              onClick={() => setShowReportModal(true)}
              style={{background:"rgba(255,60,60,0.15)",border:"1px solid rgba(255,60,60,0.3)",color:"#ff6b6b",borderRadius:"6px",padding:"4px 8px",fontSize:"11px",cursor:"pointer",fontWeight:600,display:"flex",alignItems:"center",gap:"3px"}}
              title="דיווח על החשבון"
            >
              🚩
            </button>
          </div>
        </div>

        {/* Pending call notice */}
        {callPending && (
          <div className="match-call-pending">
            <div className="match-call-pending-spinner"></div>
            <span>ממתינים ל־ {match.partner_username} לאישור השיחה…</span>
            <button
              className="match-call-cancel"
              onClick={() => {
                pendingCall.current = false;
                setCallPending(false);
                socket?.emit('match_call_cancel', { to_user_id: match.partner_id });
              }}
            >
              ביטול
            </button>
          </div>
        )}

        {/* Messages */}
        <div className="match-chatroom-messages">
          {messages.length === 0 && (
            <div className="match-chatroom-empty">
              <p>עדיין אין הודעות. זה הזמן להגיד שלום! 👋</p>
            </div>
          )}
          {messages.map((msg) => {
            const isMine = msg.sender_id === currentUser.id;
            return (
              <div key={msg.id} className={`match-msg ${isMine ? 'mine' : 'theirs'}`}>
                <div className="match-msg-bubble">{msg.text}</div>
                <div className="match-msg-time">
                  {msg.timestamp
                    ? new Date(msg.timestamp).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })
                    : ''}
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        {error && <div role="alert" className="inline-error">{error} <button onClick={reload}>טעינת השיחה מחדש</button></div>}
        {/* Input */}
        <div className="match-chatroom-input">
          <input
            type="text"
            aria-label="הודעה"
            maxLength={500}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={`הודעה אל ${match.partner_username}…`}
          />
          <button onClick={sendMessage} disabled={sending || !newMessage.trim()}>
            {sending ? 'שולחים…' : "שליחה"}
          </button>
        </div>
      </div>

      {/* Partner profile modal — shown when clicking the partner's name */}
      {viewingProfile && (
        <PartnerProfileModal
          partnerId={match.partner_id}
          partnerUsername={match.partner_username}
          sessionId={match.session_id}
          currentUser={currentUser}
          socket={socket}
          token={ctxToken}
          onClose={() => setViewingProfile(false)}
          onUnmatch={handleMatchUnmatch}
        />
      )}

      {/* Report Modal */}
      {showReportModal && (
        <ReportModal
          partnerId={match.partner_id}
          partnerUsername={match.partner_username}
          token={ctxToken || localStorage.getItem('token')}
          onClose={() => setShowReportModal(false)}
        />
      )}
    </div>
  );
};

export default MatchChatRoom;
